import {
  Autocomplete,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useController,
} from "react-hook-form"
import { CustomHelperText } from "ui"
import ActiveCheckbox from "ui/icons/ActiveCheckbox"
import CheckboxIcon from "ui/icons/CheckboxIcon"
import {
  CheckboxRow,
  StyledDivider,
  StyledHeader,
  StyledInputLabel,
  StyledSelect,
  StyledSelectMenuProps,
} from "./styles"

export function formatDate(date?: Date, time?: string) {
  if (!date) {
    return undefined
  }
  const eventDate = new Date(date)
  if (time) {
    const [hours, minutes] = time.split(":")
    eventDate.setHours(parseInt(hours))
    eventDate.setMinutes(parseInt(minutes))
  }

  return new Date(eventDate.getTime() - eventDate.getTimezoneOffset() * 60000)
}

// Helper function to render label with optional required asterisk
const renderLabel = (label: string, required?: boolean) => (
  <>
    <span style={{ color: "#051711" }}>{label}</span>
    {required && <span style={{ color: "#E00700" }}>*</span>}
  </>
)

export const notAfterNowIfToday = (value?: string, lostDate?: string) => {
  if (!value || !lostDate) return true
  const inputDate = new Date(lostDate)
  const now = new Date()
  if (inputDate.toDateString() === now.toDateString()) {
    const [hours, minutes] = value.split(":").map(Number)
    if (
      hours > now.getHours() ||
      (hours === now.getHours() && minutes > now.getMinutes())
    ) {
      return false
    }
  }
  return true
}

export const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY

if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

interface CustomTextFieldProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  type?: string
  multiline?: boolean
  rows?: number
  fullWidth?: boolean
  isSubmitted?: boolean
  slotProps?: any
  sx?: any
  errors?: any
}

export const CustomTextField = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  type = "text",
  multiline = false,
  rows,
  fullWidth = true,
  isSubmitted = false,
  slotProps,
  sx,
  errors,
}: CustomTextFieldProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label={renderLabel(label, required)}
          type={type}
          variant="filled"
          error={!!errors?.[name]}
          helperText={
            <CustomHelperText
              render={isSubmitted}
              error={!!errors?.[name]}
              text={errors?.[name]?.message}
            />
          }
          multiline={multiline}
          rows={rows}
          slotProps={slotProps}
          sx={{
            marginBlockEnd: "24px",
            ...sx,
          }}
          fullWidth={fullWidth}
        />
      )}
    />
  )
}

interface CustomSelectProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  fullWidth?: boolean
  isSubmitted?: boolean
  options: { value: number | string; label: string }[]
  sx?: any
  errors?: any
}

export const CustomSelect = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  fullWidth = true,
  isSubmitted = false,
  options,
  sx,
  errors,
}: CustomSelectProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth={fullWidth}
          error={!!errors?.[name]}
          sx={{
            marginBlockEnd: "24px",
            ...sx,
          }}
        >
          <StyledInputLabel>{renderLabel(label, required)}</StyledInputLabel>
          <StyledSelect
            {...field}
            variant="filled"
            MenuProps={StyledSelectMenuProps}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
          <CustomHelperText
            render={isSubmitted}
            error={!!errors?.[name]}
            text={errors?.[name]?.message}
          />
        </FormControl>
      )}
    />
  )
}

interface Option {
  value: number | string
  label: string
}

interface CustomAutocompleteProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  fullWidth?: boolean
  isSubmitted?: boolean
  options: Option[]
  sx?: any
  errors?: any
  onInputChange?: (value: string) => void
  loading?: boolean
  loadingText: string
  noOptionsText: string
  startTypingText: string
}

export const CustomAutocomplete = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  fullWidth = true,
  isSubmitted = false,
  options,
  sx,
  errors,
  onInputChange,
  loading = false,
  loadingText,
  noOptionsText,
  startTypingText,
}: CustomAutocompleteProps<T>) => {
  const [inputValue, setInputValue] = useState("")

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        return (
          <FormControl
            fullWidth={fullWidth}
            error={!!errors?.[name]}
            sx={{
              marginBlockEnd: "24px",
              ...sx,
            }}
          >
            <Autocomplete<Option, false, false, false>
              id={`autocomplete-${name}`}
              options={options}
              loading={loading}
              value={
                options.find(
                  (opt) => String(opt.value) === String(field.value),
                ) || null
              }
              autoHighlight={false}
              autoSelect={false}
              onChange={(_, newValue) => {
                field.onChange(newValue ? newValue.value : null)
              }}
              onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue)
                if (reason === "input") {
                  onInputChange?.(newInputValue)
                }
              }}
              filterOptions={(options, state) => options}
              renderOption={(props, option) => (
                <MenuItem
                  {...props}
                  key={option.value}
                  style={{ height: "67px", whiteSpace: "normal" }}
                >
                  {option.label}
                </MenuItem>
              )}
              loadingText={loadingText}
              noOptionsText={
                inputValue === "" ? startTypingText : noOptionsText
              }
              slotProps={{
                paper: {
                  sx: {
                    ...StyledSelectMenuProps.PaperProps.sx,
                    overflowY: "hidden",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={renderLabel(label, required)}
                  variant="filled"
                  error={!!errors?.[name]}
                  helperText={
                    <CustomHelperText
                      render={isSubmitted}
                      error={!!errors?.[name]}
                      text={errors?.[name]?.message}
                    />
                  }
                />
              )}
            />
          </FormControl>
        )
      }}
    />
  )
}

interface MultilineCustomTextFieldProps<T extends FieldValues>
  extends Omit<CustomTextFieldProps<T>, "multiline" | "rows"> {
  minRows?: number
  maxRows?: number
}

export const MultilineCustomTextField = <T extends FieldValues>({
  minRows = 3,
  maxRows = 6,
  ...props
}: MultilineCustomTextFieldProps<T>) => {
  const [count, setCount] = useState(0)
  const { field } = useController({ name: props.name, control: props.control })
  const maxLength = 250

  useEffect(() => {
    setCount(field.value?.length || 0)
  }, [field.value])

  return (
    <div>
      <CustomTextField
        {...props}
        multiline
        rows={6}
        sx={{ maxWidth: "736px", marginBlockEnd: "0" }}
        slotProps={{
          input: {
            style: {
              resize: "vertical",
              fontSize: "16px",
              marginBlockEnd: "0",
            },
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "right",
          maxWidth: "736px",
          marginTop: "4px",
        }}
      >
        {count}/{maxLength}
      </Typography>
    </div>
  )
}

interface DateControllerProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  isSubmitted?: boolean
  errors?: any
  sx?: any
  midnightText?: string
  okButtonLabel: string
  cancelButtonLabel: string
  toolbarTitle: string
  locale: "he" | "en" | "ar" | "ru"
}

export const DateController = <T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  isSubmitted = false,
  errors,
  sx,
  midnightText,
  okButtonLabel,
  cancelButtonLabel,
  toolbarTitle,
  locale,
}: DateControllerProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
              width: "100%",
            }}
          >
            <DatePicker
              {...field}
              label={renderLabel(label, required)}
              disableFuture
              minDate={dayjs().subtract(3, "month")}
              localeText={{
                okButtonLabel: okButtonLabel,
                cancelButtonLabel: cancelButtonLabel,
                toolbarTitle: toolbarTitle,
              }}
              slotProps={{
                textField: {
                  variant: "filled",
                  fullWidth: true,
                  error: !!errors?.[name],
                  autoComplete: "bday",
                  helperText: (
                    <CustomHelperText
                      render={isSubmitted}
                      error={!!errors?.[name]}
                      text={errors?.[name]?.message}
                    />
                  ),
                  sx: {
                    ...sx,
                  },
                },
              }}
            />
            <Typography fontSize={14} variant="body4">
              {midnightText}
            </Typography>
          </div>
        </LocalizationProvider>
      )}
    />
  )
}

interface NumbersControllerProps<T extends FieldValues> {
  prefixName: Path<T>
  phoneName: Path<T>
  control: Control<T>
  label: string
  required?: boolean
  isSubmitted?: boolean
  errors?: any
  sx?: any
  prefixOptions?: { value: string; label: string }[]
  locale: "he" | "en" | "ar" | "ru"
}

export const prefixOptionsArray = [
  { value: "050", label: "050" },
  { value: "052", label: "052" },
  { value: "053", label: "053" },
  { value: "054", label: "054" },
  { value: "055", label: "055" },
  { value: "058", label: "058" },
]

export const NumbersController = <T extends FieldValues>({
  prefixName,
  phoneName,
  control,
  label,
  required = false,
  isSubmitted = false,
  errors,
  sx,
  prefixOptions = prefixOptionsArray,
  locale,
}: NumbersControllerProps<T>) => {
  const hasError = !!errors?.[phoneName]

  return (
    <div
      style={{
        display: "flex",
        flexDirection:
          locale === "he" || locale === "ar" ? "row" : "row-reverse",
        width: "100%",
      }}
    >
      <Controller
        name={phoneName}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={renderLabel(label, required)}
            type="text"
            variant="filled"
            autoComplete="tel-local"
            error={hasError}
            helperText={
              <CustomHelperText
                render={isSubmitted}
                error={hasError}
                text={errors?.[phoneName]?.message}
              />
            }
            sx={{
              marginBlockEnd: "24px",
              flex: 1,
              "& .MuiInputBase-root": {
                ...(locale === "he" || locale === "ar"
                  ? {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }),
              },
              ...sx,
            }}
            fullWidth
          />
        )}
      />
      <Controller
        name={prefixName}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            onChange={(e) => {
              field.onChange(e)
            }}
            defaultValue={prefixOptions[0].value}
            error={hasError}
            sx={{
              minwidth: "84px",
              width: "auto",
              height: "56px",
              ...(locale === "he" || locale === "ar"
                ? {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }
                : {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }),
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E00700" : undefined,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E00700" : undefined,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E00700" : undefined,
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  width: "85px",
                  boxShadow: "0px 4px 12px 0px #00000029",
                  border: "1px solid #6F747D",
                  borderRadius: "8px",
                  paddingLeft: "0px",
                  paddingRight: "0px",
                },
              },
            }}
          >
            {prefixOptions.map((option) => (
              <MenuItem
                style={{
                  padding: "16px",
                }}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </div>
  )
}

export const Header = ({ title }: { title?: string }) => {
  const tInner = useTranslations("InnerPages")
  return (
    <StyledHeader>
      <Typography variant="h4">{title || tInner("personalDetails")}</Typography>
      <StyledDivider />
      <Typography variant="body2">
        {tInner("mandatoryFieldWithAsterisk")}
        <span style={{ color: "#E00700" }}>*</span>
      </Typography>
    </StyledHeader>
  )
}

export const isFieldRequired = (schema: any, fieldName: string): boolean => {
  const field = schema.describe().fields[fieldName] as any

  if (field.optional) {
    return false
  }

  if (field.type === "date") {
    return !field.nullable
  }

  const hasRequiredTest =
    field.tests?.some(
      (item: any) => item.name === "required" || item.name === "defined",
    ) ?? false

  return hasRequiredTest || (!field.nullable && !field.optional)
}

interface CustomCheckboxProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  label: string
}

export const CustomCheckbox = <T extends FieldValues>({
  name,
  control,
  label,
}: CustomCheckboxProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <CheckboxRow>
            <Checkbox
              {...field}
              icon={<CheckboxIcon />}
              checkedIcon={<ActiveCheckbox />}
              checked={!!field.value} // Ensure checked is boolean
              sx={{
                marginRight: "-8px",
                ":hover": {
                  backgroundColor: "transparent",
                },
              }}
              onChange={(e) => {
                field.onChange(e.target.checked)
              }}
            />
            <Typography variant="body2">{label}</Typography>
          </CheckboxRow>
        )
      }}
    />
  )
}
